import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Accordion from "../../../../../components/accordion/Accordion";
import AddNewItemButton from "../../../../../components/buttons/AddNewItemButton";
import ConfirmationModal from "../../../../../components/modals/ConfirmationModal";
import {
  updateAppointmentTypeErrors,
  updateAppointmentTypes,
  updateOpenAppointmentTypes,
} from "../../../../../redux/actions/CalendarManagementActions";
import {
  getAppointmentTypeErrors,
  getAppointmentTypeFieldError,
  isEmptyAppointmentTypeSection,
  validateAppointmentType,
} from "../../../../../services-logic/calendar-management/calendar-management-logic";
import { postOpenModal } from "../../../../../utils/utilPostMessage";
import AppointmentTypesAccordionBody from "./AppointmentTypesAccordionBody";
import AppointmentTypesAccordionHeader from "./AppointmentTypesAccordionHeader";

const AppointmentTypesAccordion = () => {
  const dispatch = useDispatch();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);

  const { externalOverlay } = useSelector((state) => state.info);
  const { appointmentTypeErrors, config, openAppointmentTypes, updatedAppointmentTypes } = useSelector(
    (state) => state.calendarManagement
  );

  const [modalStyles, setModalStyles] = useState("");
  const headerRefs = useRef([]); // Array to hold refs for each header

  // Initialize headerRefs based on updatedAppointmentTypes length
  useEffect(() => {
    headerRefs.current = updatedAppointmentTypes.map((_, i) => headerRefs.current[i] || React.createRef());
  }, [updatedAppointmentTypes]);

  useEffect(() => {
    const appointmentTypeErrors = updatedAppointmentTypes.map((appointmentType) => {
      return getAppointmentTypeErrors(appointmentType);
    });
    dispatch(updateAppointmentTypeErrors(appointmentTypeErrors));
  }, []);

  const onUpdateOpenAppointmentTypes = (openAppointmentTypes) => {
    dispatch(updateOpenAppointmentTypes(openAppointmentTypes));
  };

  const onAddNewAppointmentType = () => {
    const newAppointmentType = {
      type: "",
      description: "",
      duration: null,
      postSchedulingTextMessage: "",
    };

    const newAppointmentTypes = [...updatedAppointmentTypes, newAppointmentType];
    dispatch(updateAppointmentTypes(newAppointmentTypes));
    onUpdateOpenAppointmentTypes([newAppointmentTypes.length - 1]);
  };

  const onAppointmentTypeUpdated = (index, key, value) => {
    const newAppointmentTypes = updatedAppointmentTypes.map((item, idx) =>
      idx === index ? { ...item, [key]: value } : { ...item }
    );
    dispatch(updateAppointmentTypes(newAppointmentTypes));

    const updatedAppointmentTypeErrors = [...appointmentTypeErrors];
    updatedAppointmentTypeErrors[index] = {
      ...updatedAppointmentTypeErrors[index],
      [key]: getAppointmentTypeFieldError(newAppointmentTypes[index], key, value),
    };
    dispatch(updateAppointmentTypeErrors(updatedAppointmentTypeErrors));
  };

  const onRemoveAppointmentType = (index) => {
    const newAppointmentTypes = [...updatedAppointmentTypes];
    newAppointmentTypes.splice(index, 1);
    dispatch(updateAppointmentTypes(newAppointmentTypes));
    onUpdateOpenAppointmentTypes([]);

    const updatedAppointmentTypeErrors = [...appointmentTypeErrors];
    updatedAppointmentTypeErrors.splice(index, 1);
    dispatch(updateAppointmentTypeErrors(updatedAppointmentTypeErrors));
  };

  const showRemoveAppointmentTypeModal = (index) => {
    if (isEmptyAppointmentTypeSection(updatedAppointmentTypes[index])) {
      onRemoveAppointmentType(index);
      return;
    }

    if (externalOverlay) {
      const message = {
        childrenType: "confirmation",
        id: "delete-appointment-type",
        index: index,
        headerTitle: "Delete Appointment Type",
        bodyMessage: `Are you sure you want to delete this type: ${updatedAppointmentTypes[index].type}?`,
        cancelText: "Cancel",
        confirmText: "Delete",
      };

      postOpenModal(message);
    } else {
      const headerRef = headerRefs.current[index];
      if (headerRef && headerRef.current && typeof headerRef.current.getBoundingClientRect === "function") {
        const accordionRect = headerRef.current.getBoundingClientRect();

        setModalStyles({
          position: "absolute",
          top: `${accordionRect.top - 10}px `,
          left: "50%",
          transform: "translateX(-50%)",
        });
      } else {
        setModalStyles({});
      }

      setIsConfirmationModalOpen(true);
      setDeletingIndex(index);
    }
  };

  useEffect(() => {
    if (externalOverlay) {
      const handleMessage = (event) => {
        const data = event.data;
        if (data.type === "overlay-to-snippet") {
          const extra = data.extra;
          if (extra.id === "delete-appointment-type" && extra.action === "confirm") {
            onRemoveAppointmentType(extra.index);
          }
        }
      };

      // Add the event listener
      window.addEventListener("message", handleMessage);

      // Clean up the event listener on component unmount or when externalOverlay changes
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, [externalOverlay, onRemoveAppointmentType]);

  const headerChildren = ({ item, index, isActive }) => {
    return (
      <AppointmentTypesAccordionHeader
        ref={headerRefs.current[index]}
        item={item}
        index={index}
        isActive={isActive}
        onRemoveAppointmentType={showRemoveAppointmentTypeModal}
      />
    );
  };

  const bodyChildren = ({ item, index }) => {
    return (
      <AppointmentTypesAccordionBody item={item} index={index} onAppointmentTypeUpdated={onAppointmentTypeUpdated} />
    );
  };

  const isAllAppointmentTypesValid = useMemo(
    () => updatedAppointmentTypes?.every((appointmentType) => validateAppointmentType(null, appointmentType).valid),
    [updatedAppointmentTypes]
  );

  return (
    <div className="w-full flex flex-col gap-4">
      <Accordion
        id="appointment-types"
        data={updatedAppointmentTypes || []}
        headerChildren={headerChildren}
        bodyChildren={bodyChildren}
        activeIndexes={openAppointmentTypes}
        setActiveIndexes={onUpdateOpenAppointmentTypes}
      />
      <div className="flex flex-col md:flex-row justify-start items-start md:items-center gap-2">
        <AddNewItemButton
          title="+ Add appointment type"
          onClick={onAddNewAppointmentType}
          width="w-64"
          disabled={!isAllAppointmentTypesValid || updatedAppointmentTypes?.length >= config?.maxAppointmentTypesListSize}
          onDisabledTooltip={
            !isAllAppointmentTypesValid && updatedAppointmentTypes?.length < config?.maxAppointmentTypesListSize
              ? "Finish filling out incomplete items before adding a new one"
              : ""
          }
        />
        {updatedAppointmentTypes?.length >= config?.maxAppointmentTypesListSize && (
          <span className="text-[10px] md:text-[12px]">
            You have reached the maximum number of appointment types allowed for your account.
          </span>
        )}
      </div>
      {!externalOverlay && isConfirmationModalOpen && (
        <ConfirmationModal
          onClose={() => setIsConfirmationModalOpen(false)}
          dataFromSnippet={{
            headerTitle: "Delete Appointment Type",
            bodyMessage: `Are you sure you want to delete this type: ${updatedAppointmentTypes[deletingIndex].type}?`,
            cancelText: "Cancel",
            confirmText: "Delete",
          }}
          onConfirm={(event) => onRemoveAppointmentType(event, deletingIndex)}
          onCancel={() => setIsConfirmationModalOpen(false)}
          modalStyles={modalStyles}
        />
      )}
    </div>
  );
};

export default AppointmentTypesAccordion;
